/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Button,
  ButtonGroup,
  Circle,
  Flex,
  Grid,
  GridItem,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { FormProvider, SubmitHandler, useForm, useFormContext } from 'react-hook-form';

import type { NotificationCreateInput } from '@/client/services/api/graphql/gql/graphql';
import { Plus } from '@/client/components/icons/ContinuIcons';
import SolidButton from '@/client/components/shared/buttons/SolidButton';
import { useNotificationEditor } from '@/client/services/hooks/admin/notifications/useNotificationEditor';
import { useState } from 'react';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

type InAppMessageFormValues = {
  body: string;
};

interface AssignmentNotificationEmailMessageFormProps {
  handleCloseModal: () => void;
}

export default function AssignmentNotificationInAppMessageForm({
  handleCloseModal,
}: AssignmentNotificationEmailMessageFormProps) {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const [cursorPosition, setCursorPosition] = useState(0);
  const { parseTemplate } = useNotificationEditor();

  const { watch: parentFormWatch, setValue: parentFormSetValue } =
    useFormContext<NotificationCreateInput>();

  const { messaging } = parentFormWatch();

  const defaultBody = messaging.inApp.find((setting) => setting.name === 'body')?.value;

  const methods = useForm<InAppMessageFormValues>({
    defaultValues: {
      body: defaultBody,
    },
    mode: 'onChange',
  });

  const {
    register,
    watch,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = methods;

  const hasError = !!errors.body;

  const { body } = watch();

  // TODO: Move these to useNotificationEditor hook
  const dynamicContentOptions = {
    due_in: t('notifications.dynamicContent.dueIn'),
    assigner_first_name: t('notifications.dynamicContent.assignerFirstName'),
    assigner_last_name: t('notifications.dynamicContent.assignerLastName'),
    assignee_first_name: t('notifications.dynamicContent.assigneeFirstName'),
    assignee_last_name: t('notifications.dynamicContent.assigneeLastName'),
    recipient_first_name: t('notifications.dynamicContent.recipientFirstName'),
    recipient_last_name: t('notifications.dynamicContent.recipientLastName'),
  };

  const onSubmit: SubmitHandler<InAppMessageFormValues> = (data) => {
    parentFormSetValue('messaging.inApp', [{ name: 'body', value: data.body }]);

    reset();
    handleCloseModal();
  };

  const onSubmitError = () =>
    setToast({
      show: true,
      status: 'error',
      title: t('notifications.errors'),
    });

  const insertVariable = (variable: string) => {
    const currentBodyValue = body;

    return setValue(
      'body',
      currentBodyValue.slice(0, cursorPosition) + variable + currentBodyValue.slice(cursorPosition),
    );
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onSubmitError)}>
        <Box>
          <Grid templateColumns="repeat(4, 1fr)" gap={24} padding={8}>
            <GridItem colSpan={2}>
              <Stack spacing={2}>
                <Flex alignItems="center" justifyContent="space-between">
                  <Text variant="createLabel">{t('notifications.messaging.inApp.message')}</Text>

                  <Menu>
                    <MenuButton
                      as={Button}
                      variant="ghost"
                      fontSize="12px"
                      fontWeight={600}
                      color="neutral.1200"
                      leftIcon={<Plus />}
                    >
                      {t('notifications.messaging.addDynamicContent')}
                    </MenuButton>

                    <MenuList borderColor="warmNeutral.200">
                      {Object.entries(dynamicContentOptions).map(([key, value]) => (
                        <MenuItem
                          key={key}
                          onClick={() => {
                            insertVariable(` {${key}}`);
                          }}
                        >
                          {value}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </Flex>

                <Textarea
                  {...register('body', { required: true })}
                  placeholder={`${t('notifications.messaging.inApp.message.placeholder')}`}
                  onBlur={(e) => setCursorPosition(e.target.selectionStart)}
                  _focusVisible={{
                    borderColor: hasError ? 'error' : 'warmNeutral.700',
                  }}
                />
              </Stack>
            </GridItem>

            <GridItem colSpan={2}>
              <Box
                backgroundColor="warmNeutral.0"
                border="1px solid"
                borderColor="warmNeutral.200"
                borderRadius="32px"
                paddingX={6}
                paddingY={10}
              >
                <Box
                  backgroundColor="white"
                  border="1px solid"
                  borderColor="neutral.200"
                  borderRadius="16px"
                  padding={6}
                >
                  <HStack>
                    <Circle size={50} backgroundColor="neutral.100" />

                    <Text fontSize="14px" fontWeight={400} whiteSpace="pre-wrap">
                      {parseTemplate(body, 'slack')}
                    </Text>
                  </HStack>
                </Box>
              </Box>
            </GridItem>
          </Grid>

          <Flex
            width="full"
            borderTop="1px solid"
            borderColor="neutral.100"
            justifyContent="flex-end"
            paddingX={6}
            paddingY={4}
            position="sticky"
            bottom={0}
            backgroundColor="white"
          >
            <ButtonGroup>
              <Button
                variant="cancel"
                onClick={() => {
                  reset();
                  handleCloseModal();
                }}
              >
                {t('global.actions.cancel')}
              </Button>

              <SolidButton type="submit">
                {t('notifications.messaging.inApp.saveChanges')}
              </SolidButton>
            </ButtonGroup>
          </Flex>
        </Box>
      </form>
    </FormProvider>
  );
}
